import React, { useState, lazy, Suspense } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Compass, Sparkles, Layers, Rocket, ArrowRight } from 'lucide-react';

const CalendarModal = lazy(() => import('./CalendarModal'));

const Process = () => {
  const { t, language } = useLanguage();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const steps = [
    {
      icon: Compass,
      number: '01',
      title: t.process.steps.discovery.title,
      description: t.process.steps.discovery.description,
      highlight: t.common.free
    },
    {
      icon: Sparkles,
      number: '02',
      title: t.process.steps.prototype.title,
      description: t.process.steps.prototype.description,
      highlight: t.common.free
    },
    {
      icon: Layers,
      number: '03',
      title: t.process.steps.development.title,
      description: t.process.steps.development.description
    },
    {
      icon: Rocket,
      number: '04',
      title: t.process.steps.launch.title,
      description: t.process.steps.launch.description
    }
  ];

  return (
    <>
      <section
        id="process"
        data-testid="process-section"
        className="relative py-20 lg:py-28 overflow-hidden bg-[#FAFAFA] border-t border-slate-100"
      >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="stripe-badge mb-4 inline-flex">
              <span>🚀</span>
              <span>{t.process.badge}</span>
            </div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl mb-4 tracking-tight text-[#0F172A]">
              {t.process.title}{' '}
              <span className="bg-gradient-to-r from-[#4F46E5] to-[#7C3AED] bg-clip-text text-transparent">
                {t.process.titleHighlight}
              </span>
            </h2>
            {t.process.subtitle && (
              <p className="text-base lg:text-lg text-slate-600 leading-relaxed font-normal">
                {t.process.subtitle}
              </p>
            )}
          </div>

          {/* Process Steps */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div
                  key={index}
                  data-testid={`process-step-${index}`}
                  className="relative group"
                >
                  <div className="stripe-card p-7 h-full flex flex-col justify-between relative">
                    {/* Free badge */}
                    {step.highlight && (
                      <div className="absolute -top-3 right-6 px-3 py-0.5 rounded-full bg-[#10B981] text-white text-[11px] font-bold shadow-sm tracking-wider">
                        {step.highlight}
                      </div>
                    )}

                    <div>
                      {/* Step Number & Icon */}
                      <div className="flex items-center justify-between mb-5">
                        <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-[#4F46E5] group-hover:scale-110 transition-transform">
                          <Icon className="w-6 h-6" />
                        </div>
                        <div className="text-3xl font-heading font-extrabold text-slate-200">
                          {step.number}
                        </div>
                      </div>

                      {/* Content */}
                      <h3 className="font-heading font-bold text-lg mb-2 text-[#0F172A] group-hover:text-[#4F46E5] transition-colors">
                        {step.title}
                      </h3>
                      <p className="text-slate-500 text-sm leading-relaxed font-normal">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <div className="mt-14 text-center">
            <button
              onClick={() => setIsCalendarOpen(true)}
              className="inline-flex flex-col sm:flex-row items-center gap-4 sm:gap-6 p-5 px-8 rounded-2xl bg-white border border-slate-200/80 hover:border-[#4F46E5]/40 transition-all shadow-sm hover:shadow-md group cursor-pointer"
            >
              <div className="text-center sm:text-left">
                <div className="font-heading font-bold text-base text-[#0F172A] group-hover:text-[#4F46E5] transition-colors">
                  {language === 'es' ? '¿Listo para evaluar tu proyecto o producto digital?' : 'Ready to evaluate your project or digital product?'}
                </div>
                <div className="text-xs text-slate-500 font-medium">
                  {language === 'es' ? 'Conversación y prototipo inicial sin costo ni compromiso' : 'Free conversation and initial prototype, no commitment'}
                </div>
              </div>
              <div className="btn-stripe-dark text-xs py-2 px-5">
                <span>{language === 'es' ? 'Agendar Ahora' : 'Schedule Now'}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </button>
          </div>
        </div>
      </section>

      {isCalendarOpen && (
        <Suspense fallback={null}>
          <CalendarModal isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} />
        </Suspense>
      )}
    </>
  );
};

export default Process;
